import {
  FilterDocument,
  GroupByMode,
  HabitGroupBy,
  HabitSortingMode,
  SortingMode,
} from '@griply/types';
import { useUpdateHabitGroupBy, useUpdateHabitSortMode } from 'features/user';
import { useCallback, useMemo } from 'react';
import { useUser } from 'shared/hooks/use-user';
import { buildHabitsFilterDocument } from 'shared/utils/build-habits-filter-document';
import { getHabitsSidePanelCustomOrder } from 'shared/utils/get-habits-side-panel-custom-order';

const groupByModeToHabitGroupBy: Partial<Record<GroupByMode, HabitGroupBy>> = {
  [GroupByMode.None]: HabitGroupBy.None,
  [GroupByMode.Goal]: HabitGroupBy.Goal,
  [GroupByMode.LifeArea]: HabitGroupBy.LifeArea,
  [GroupByMode.Priority]: HabitGroupBy.Priority,
};

const sortingModeToHabitSortingMode: Partial<
  Record<SortingMode, HabitSortingMode>
> = {
  [SortingMode.Custom]: HabitSortingMode.Custom,
  [SortingMode.Priority]: HabitSortingMode.Priority,
};

export const useSidePanelHabits = () => {
  const user = useUser();
  const updateHabitGroupBy = useUpdateHabitGroupBy();
  const updateHabitSortMode = useUpdateHabitSortMode();

  const habitGroupBy = user?.settings?.habitGroupBy;
  const habitSortingMode = user?.settings?.habitSortingMode;
  const habitSorting = user?.settings?.habitSorting;
  const habitGroupSorting = user?.settings?.habitGroupSorting;

  const filterDocument = useMemo(
    () => buildHabitsFilterDocument(habitGroupBy, habitSortingMode),
    [habitGroupBy, habitSortingMode],
  );

  const defaultFilterDocument = useMemo(
    () => buildHabitsFilterDocument(undefined, undefined),
    [],
  );

  const customOrder = useMemo(
    () =>
      getHabitsSidePanelCustomOrder(
        habitGroupBy,
        habitSorting,
        habitGroupSorting,
      ),
    [habitGroupBy, habitSorting, habitGroupSorting],
  );

  const updateFilterDocument = useCallback(
    (newFilterDocument: FilterDocument) => {
      const nextGroupBy =
        newFilterDocument.groupByMode != null
          ? groupByModeToHabitGroupBy[newFilterDocument.groupByMode]
          : undefined;
      if (nextGroupBy && nextGroupBy !== (habitGroupBy ?? HabitGroupBy.None)) {
        updateHabitGroupBy(nextGroupBy);
      }

      const nextSortMode =
        newFilterDocument.sortingMode != null
          ? sortingModeToHabitSortingMode[newFilterDocument.sortingMode]
          : undefined;
      if (nextSortMode && nextSortMode !== habitSortingMode) {
        updateHabitSortMode(nextSortMode);
      }
    },
    [habitGroupBy, habitSortingMode, updateHabitGroupBy, updateHabitSortMode],
  );

  const onReset = useCallback(() => {
    updateHabitGroupBy(HabitGroupBy.None);
    updateHabitSortMode(HabitSortingMode.Custom);
  }, [updateHabitGroupBy, updateHabitSortMode]);

  return {
    filterDocument,
    defaultFilterDocument,
    customOrder,
    updateFilterDocument,
    onReset,
  };
};
